import { useEffect, useState } from "react";
import { format } from "date-fns";
import { Search } from "lucide-react";
import { Button as TamaguiButton, XStack, YStack } from "tamagui";
import { useNavigate } from "react-router-dom";

import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { AdminPageScaffold, AdminStatusPill } from "@/components/AdminScaffold";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  AppCard,
  AppCardBody,
  AppEmptyState,
  AppInput,
  AppLoadingState,
  AppText,
  kindredFontFamilies,
} from "../../ui-foundation";

type MemberSummary = Pick<
  Database["public"]["Views"]["admin_profiles"]["Row"],
  "id" | "display_name" | "postcode" | "avatar_url" | "account_status" | "created_at"
>;

type RoleAssignment = Pick<Database["public"]["Tables"]["user_roles"]["Row"], "user_id" | "role">;

const AdminMembers = () => {
  const navigate = useNavigate();
  const [members, setMembers] = useState<MemberSummary[]>([]);
  const [roles, setRoles] = useState<Record<string, string>>({});
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMembers = async () => {
      const [{ data: profiles }, { data: userRoles }] = await Promise.all([
        supabase
          .from("admin_profiles")
          .select("id, display_name, postcode, avatar_url, account_status, created_at")
          .order("created_at", { ascending: false }),
        supabase.from("user_roles").select("user_id, role"),
      ]);

      setMembers(profiles || []);
      const roleMap: Record<string, string> = {};
      (userRoles || []).forEach((roleRow: RoleAssignment) => {
        roleMap[roleRow.user_id] = roleRow.role;
      });
      setRoles(roleMap);
      setLoading(false);
    };

    void fetchMembers();
  }, []);

  const filteredMembers = search.trim()
    ? members.filter((member) =>
        (member.display_name || "").toLowerCase().includes(search.toLowerCase()) ||
        (member.postcode || "").toLowerCase().includes(search.toLowerCase()),
      )
    : members;

  const roleTone = (role: string) => {
    if (role === "admin") return "primary";
    if (role === "moderator") return "secondary";
    return "neutral";
  };

  const statusTone = (status: string) => {
    if (status === "suspended") return "warning";
    if (status === "banned") return "danger";
    return "neutral";
  };

  return (
    <AdminPageScaffold
      title="Members"
      subtitle="Review recent signups and member account status."
    >
      <AppCard>
        <AppCardBody>
          <XStack alignItems="center" gap="$sm">
            <Search size={18} className="text-muted-foreground" />
            <AppInput
              flex={1}
              value={search}
              onChangeText={setSearch}
              placeholder="Search members"
            />
          </XStack>
        </AppCardBody>
      </AppCard>

      {loading ? (
        <AppLoadingState
          title="Loading members"
          body="Fetching member profiles, roles, and account status."
        />
      ) : filteredMembers.length === 0 ? (
        <AppEmptyState
          title="No matching members"
          body={search.trim() ? "Try a different name or postcode." : "There are no members to review yet."}
        />
      ) : (
        <YStack gap="$sm">
          {filteredMembers.map((member) => (
            <TamaguiButton
              key={member.id}
              unstyled
              type="button"
              width="100%"
              onPress={() => navigate(`/admin/members/${member.id}`)}
            >
              <AppCard>
                <AppCardBody py="$sm">
                  <XStack alignItems="center" gap="$sm">
                    <Avatar className="h-[52px] w-[52px] border border-border/80 bg-primarySoft">
                      {member.avatar_url ? <AvatarImage src={member.avatar_url} alt={member.display_name || "Member"} /> : null}
                      <AvatarFallback className="bg-primarySoft text-base font-semibold text-primary">
                        {(member.display_name || "M").trim().charAt(0).toUpperCase() || "M"}
                      </AvatarFallback>
                    </Avatar>
                    <YStack flex={1} minWidth={0} gap={4}>
                      <AppText fontSize="$md" lineHeight="$md" fontFamily={kindredFontFamilies.sansSemi}>
                        {member.display_name || "No name"}
                      </AppText>
                      <AppText color="$colorMuted" fontSize="$sm" lineHeight="$sm">
                        Joined {format(new Date(member.created_at), "MMM yyyy")}
                        {member.postcode ? ` · ${member.postcode}` : ""}
                      </AppText>
                    </YStack>
                    <YStack gap={6} alignItems="flex-end">
                      <AdminStatusPill tone={roleTone(roles[member.id] || "user")}>
                        {roles[member.id] === "admin"
                          ? "Admin"
                          : roles[member.id] === "moderator"
                            ? "Moderator"
                            : "Member"}
                      </AdminStatusPill>
                      <AdminStatusPill tone={statusTone(member.account_status)}>
                        {member.account_status === "suspended"
                          ? "Suspended"
                          : member.account_status === "banned"
                            ? "Banned"
                            : "Active"}
                      </AdminStatusPill>
                    </YStack>
                  </XStack>
                </AppCardBody>
              </AppCard>
            </TamaguiButton>
          ))}
        </YStack>
      )}
    </AdminPageScaffold>
  );
};

export default AdminMembers;
